import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../firebase/config/firebase';
import { StudentProfile, StudentReferralStats, ReferralRecord } from '../types';

/**
 * Clean helper to transliterate / format student first name to alphanumeric code
 */
const cleanNameForCode = (fullName: string): string => {
  if (!fullName) return 'STUDENT';
  const firstWord = fullName.trim().split(' ')[0] || '';
  // Mapping popular Arabic names to English codes
  const ARABIC_TO_LATIN: Record<string, string> = {
    'أحمد': 'AHMED',
    'احمد': 'AHMED',
    'محمد': 'MOHAMED',
    'محمود': 'MAHMOUD',
    'عمر': 'OMAR',
    'يوسف': 'YOUSSEF',
    'علي': 'ALI',
    'علياء': 'ALIAA',
    'سارة': 'SARA',
    'ساره': 'SARA',
    'مريم': 'MARIAM',
    'نور': 'NOOR',
    'كريم': 'KAREEM',
    'مصطفى': 'MOSTAFA',
    'إبراهيم': 'IBRAHIM',
    'ابراهيم': 'IBRAHIM',
    'خالد': 'KHALED',
    'طارق': 'TAREK',
    'عبدالرحمن': 'ABDALLAH',
    'وهدان': 'WHDAN',
    'نشجو': 'NASHGO',
  };

  const mapped = ARABIC_TO_LATIN[firstWord];
  if (mapped) return mapped;

  // If already latin characters
  const latin = firstWord.replace(/[^a-zA-Z]/g, '').toUpperCase();
  if (latin.length >= 3) return latin.slice(0, 7);

  return 'STUDENT';
};

/**
 * Generate sequence of consecutive months starting from the next month
 * e.g., if now is 2026-10, count=2 => ['2026-11', '2026-12']
 */
export const calculateConsecutiveDiscountMonths = (count: number, fromDate: Date = new Date()): string[] => {
  if (count <= 0) return [];
  const months: string[] = [];
  const d = new Date(fromDate);

  for (let i = 1; i <= count; i++) {
    const nextDate = new Date(d.getFullYear(), d.getMonth() + i, 1);
    const y = nextDate.getFullYear();
    const m = String(nextDate.getMonth() + 1).padStart(2, '0');
    months.push(`${y}-${m}`);
  }

  return months;
};

export const referralService = {
  /**
   * Generate a unique Promo / Referral Code for a student
   */
  generateCode(fullName: string, uid: string): string {
    const namePart = cleanNameForCode(fullName);
    const numPart = Math.floor(10 + Math.random() * 90);
    const uidSnippet = uid.replace(/[^a-zA-Z0-9]/g, '').slice(-2).toUpperCase() || 'X1';
    return `OTO-${namePart}${numPart}${uidSnippet}`.toUpperCase();
  },

  /**
   * Get or initialize student's referral code
   */
  async getOrCreateReferralCode(student: StudentProfile): Promise<string> {
    if (student.referralCode) {
      return student.referralCode;
    }

    const code = this.generateCode(student.fullName, student.uid);
    try {
      const studentRef = doc(db, 'students', student.uid);
      await setDoc(studentRef, { referralCode: code }, { merge: true });
    } catch (err) {
      console.warn('Could not persist referralCode in Firestore:', err);
    }
    return code;
  },

  /**
   * Validate if a referral code exists and belongs to a real student
   */
  async validateReferralCode(
    code: string,
    currentStudentUid?: string
  ): Promise<{ valid: boolean; inviterUid?: string; inviterName?: string; error?: string }> {
    const cleanCode = code.trim().toUpperCase();
    if (!cleanCode) {
      return { valid: false, error: 'كود الدعوة فارغ' };
    }

    try {
      const q = query(collection(db, 'students'), where('referralCode', '==', cleanCode));
      const snap = await getDocs(q);
      if (snap.empty) {
        return { valid: false, error: 'كود الدعوة غير صحيح أو غير موجود' };
      }

      const inviterDoc = snap.docs[0];
      const inviterUid = inviterDoc.id;
      const inviterName = inviterDoc.data().fullName || 'أحد زملاء OTO';

      if (currentStudentUid && currentStudentUid === inviterUid) {
        return { valid: false, error: 'لا يمكنك استخدام كود الدعوة الخاص بك' };
      }

      return { valid: true, inviterUid, inviterName };
    } catch (err) {
      console.warn('Referral validation fallback:', err);
      return { valid: false, error: 'تعذر التحقق من كود الدعوة حالياً' };
    }
  },

  /**
   * Register a new referral link when a friend signs up with a promo code
   */
  async registerReferral(
    refereeUid: string,
    refereeName: string,
    refereePhone: string | undefined,
    referralCode: string
  ): Promise<boolean> {
    const validation = await this.validateReferralCode(referralCode, refereeUid);
    if (!validation.valid || !validation.inviterUid) {
      return false;
    }

    const referralDocId = `ref_${validation.inviterUid}_${refereeUid}`;
    const nowIso = new Date().toISOString();

    const record: ReferralRecord = {
      id: referralDocId,
      inviterUid: validation.inviterUid,
      inviterCode: referralCode.trim().toUpperCase(),
      refereeUid,
      refereeName,
      refereePhone: refereePhone || '',
      hasBooked: false,
      createdAt: nowIso,
      updatedAt: nowIso,
    };

    try {
      await setDoc(doc(db, 'referrals', referralDocId), record, { merge: true });

      // Link to referee student document
      await setDoc(
        doc(db, 'students', refereeUid),
        {
          invitedByReferralCode: referralCode.trim().toUpperCase(),
          invitedByUid: validation.inviterUid,
        },
        { merge: true }
      );

      return true;
    } catch (err) {
      console.warn('Error saving referral record:', err);
      return false;
    }
  },

  /**
   * When any student makes a booking, check if they were referred and record it
   */
  async recordBookingForReferral(studentUid: string, bookingId: string): Promise<void> {
    try {
      const q = query(
        collection(db, 'referrals'),
        where('refereeUid', '==', studentUid),
        where('hasBooked', '==', false)
      );
      const snap = await getDocs(q);

      if (!snap.empty) {
        const refDoc = snap.docs[0];
        const inviterUid = refDoc.data().inviterUid;

        await updateDoc(refDoc.ref, {
          hasBooked: true,
          firstBookingId: bookingId,
          updatedAt: new Date().toISOString(),
        });

        // Refresh inviter's referral stats and discount months
        if (inviterUid) {
          await this.recalculateStudentReferralStats(inviterUid);
        }
      }
    } catch (err) {
      console.warn('Could not record booking for referral:', err);
    }
  },

  /**
   * Recalculate inviter's stats and update their StudentProfile
   */
  async recalculateStudentReferralStats(inviterUid: string): Promise<StudentReferralStats> {
    let totalInvited = 0;
    let activeBookedCount = 0;

    try {
      const q = query(collection(db, 'referrals'), where('inviterUid', '==', inviterUid));
      const snap = await getDocs(q);

      totalInvited = snap.size;
      activeBookedCount = snap.docs.filter((d) => d.data().hasBooked).length;
    } catch (err) {
      console.warn('Error reading referrals:', err);
    }

    // Every 10 booked referrals grant 1 consecutive month of 10% discount
    const discountMonthsCount = Math.floor(activeBookedCount / 10);
    const earnedDiscountMonths = calculateConsecutiveDiscountMonths(discountMonthsCount);

    const stats: StudentReferralStats = {
      referralCode: '',
      totalInvited,
      activeBookedCount,
      earnedDiscountMonths,
    };

    try {
      const studentDoc = await getDoc(doc(db, 'students', inviterUid));
      if (studentDoc.exists()) {
        const data = studentDoc.data() as StudentProfile;
        stats.referralCode = data.referralCode || this.generateCode(data.fullName, inviterUid);

        await setDoc(
          doc(db, 'students', inviterUid),
          {
            referralStats: stats,
            referralCode: stats.referralCode,
          },
          { merge: true }
        );
      }
    } catch (err) {
      console.warn('Could not update student referral stats:', err);
    }

    return stats;
  },

  /**
   * Fetch live referral statistics for a student
   */
  async getReferralStats(student: StudentProfile): Promise<StudentReferralStats> {
    if (!student?.uid) {
      return {
        referralCode: '',
        totalInvited: 0,
        activeBookedCount: 0,
        earnedDiscountMonths: [],
      };
    }

    // Ensure referral code is set
    const code = await this.getOrCreateReferralCode(student);

    try {
      const q = query(collection(db, 'referrals'), where('inviterUid', '==', student.uid));
      const snap = await getDocs(q);
      const totalInvited = snap.size;
      const activeBookedCount = snap.docs.filter((d) => d.data().hasBooked).length;

      const discountMonthsCount = Math.floor(activeBookedCount / 10);
      const earnedDiscountMonths = calculateConsecutiveDiscountMonths(discountMonthsCount);

      const stats: StudentReferralStats = {
        referralCode: code,
        totalInvited,
        activeBookedCount,
        earnedDiscountMonths,
      };

      // Background sync to profile
      setDoc(doc(db, 'students', student.uid), { referralStats: stats }, { merge: true }).catch(() => {});

      return stats;
    } catch {
      const discountMonthsCount = Math.floor((student.referralStats?.activeBookedCount || 0) / 10);
      return {
        referralCode: code,
        totalInvited: student.referralStats?.totalInvited || 0,
        activeBookedCount: student.referralStats?.activeBookedCount || 0,
        earnedDiscountMonths: calculateConsecutiveDiscountMonths(discountMonthsCount),
      };
    }
  },

  /**
   * Check if a specific trip date qualifies for student's 10% referral discount
   */
  checkTripDiscount(
    statsOrMonths: StudentReferralStats | string[] | undefined,
    tripDateStr: string
  ): {
    hasDiscount: boolean;
    discountPercent: number;
    discountAmount: (basePrice: number) => number;
    formattedMonth: string;
  } {
    if (!tripDateStr || !statsOrMonths) {
      return { hasDiscount: false, discountPercent: 0, discountAmount: () => 0, formattedMonth: '' };
    }

    const months = Array.isArray(statsOrMonths)
      ? statsOrMonths
      : statsOrMonths.earnedDiscountMonths || [];

    if (months.length === 0) {
      return { hasDiscount: false, discountPercent: 0, discountAmount: () => 0, formattedMonth: '' };
    }

    const tripMonth = tripDateStr.slice(0, 7); // e.g. "2026-11"
    const isEligible = months.includes(tripMonth);

    const monthNames: Record<string, string> = {
      '01': 'يناير', '02': 'فبراير', '03': 'مارس', '04': 'أبريل',
      '05': 'مايو', '06': 'يونيو', '07': 'يوليو', '08': 'أغسطس',
      '09': 'سبتمبر', '10': 'أكتوبر', '11': 'نوفمبر', '12': 'ديسمبر'
    };
    const [y, m] = tripMonth.split('-');
    const formattedMonth = `${monthNames[m] || m} ${y}`;

    if (isEligible) {
      return {
        hasDiscount: true,
        discountPercent: 10,
        discountAmount: (basePrice: number) => Math.round(basePrice * 0.1),
        formattedMonth,
      };
    }

    return { hasDiscount: false, discountPercent: 0, discountAmount: () => 0, formattedMonth };
  },
};
